import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { FiEdit2, FiMessageCircle, FiPlus, FiTrash2, FiX } from 'react-icons/fi'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import { createHackSuggestionSelectedWorksNotification } from '@domain/features/user-notifications/user-notifications-feature'
import {
  createHackWhatWork,
  deleteHackWhatWork,
  deleteHackForSymptom,
  updateHackWhatWork,
  voteHackWhatWork,
} from '@domain/features/pd-hacks/pd-hacks-feature'
import type { HackWhatWork } from '@domain/features/pd-hacks/pd-hacks-feature'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import YouTubeEmbed from '@presentation/components/ui/YouTubeEmbed'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useHack } from '@presentation/hooks/useHack'
import { useHackWhatWorks } from '@presentation/hooks/useHackWhatWorks'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import { useUserProfiles } from '@presentation/hooks/useUserProfiles'
import SeoHead from '@presentation/seo/SeoHead'
import StructuredData from '@presentation/seo/StructuredData'
import { sanitizeForMeta } from '@presentation/seo/seo-constants'
import { buildHackStructuredData } from './hackStructuredData'

const INPUT_CLASS_NAME =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0! disabled:cursor-not-allowed disabled:border-border-light disabled:bg-border-light disabled:text-text-muted'

interface WhatWorkModalState {
  mode: 'create' | 'edit'
  whatWorkId?: string
  solution: string
  description: string
}

interface WhatWorkModalProps {
  state: WhatWorkModalState | null
  isSubmitting: boolean
  error: string | null
  onClose: () => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onSolutionChange: (value: string) => void
  onDescriptionChange: (value: string) => void
}

function WhatWorkModal({
  state,
  isSubmitting,
  error,
  onClose,
  onSubmit,
  onSolutionChange,
  onDescriptionChange,
}: WhatWorkModalProps) {
  if (!state) {
    return null
  }

  const isEditMode = state.mode === 'edit'
  const submitLabel = isSubmitting
    ? isEditMode
      ? 'Saving...'
      : 'Submitting...'
    : isEditMode
      ? 'Save Changes'
      : 'Submit Hack'

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close hack modal"
        onClick={onClose}
        disabled={isSubmitting}
      />
      <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={isEditMode ? 'Edit your hack' : 'Add your hack'}
          className="relative w-full max-w-2xl rounded-3xl bg-surface p-6 shadow-2xl sm:p-8"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close hack modal"
            disabled={isSubmitting}
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-background disabled:cursor-not-allowed disabled:opacity-70"
          >
            <FiX className="h-6 w-6" />
          </button>

          <h3 className="pr-10 text-2xl font-bold text-text-primary sm:text-3xl">
            {isEditMode ? 'Edit Your Hack' : 'Add Your Hack'}
          </h3>
          <p className="mt-2 text-sm text-text-secondary sm:text-base">
            Share what works for you so others can learn from your experience.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <label className="block text-sm font-semibold text-text-secondary">
              Solution
              <input
                value={state.solution}
                onChange={(event) => onSolutionChange(event.target.value)}
                className={`${INPUT_CLASS_NAME} mt-2`}
                placeholder="What helps you?"
                required
                disabled={isSubmitting}
              />
            </label>

            <label className="block text-sm font-semibold text-text-secondary">
              Description (optional)
              <textarea
                value={state.description}
                onChange={(event) => onDescriptionChange(event.target.value)}
                className={`${INPUT_CLASS_NAME} mt-2 min-h-28 resize-y`}
                placeholder="Share a bit more detail..."
                disabled={isSubmitting}
              />
            </label>

            {error ? (
              <p className="rounded-xl bg-red-500/10 px-3 py-2 text-sm text-red-500">
                {error}
              </p>
            ) : null}

            <div className="flex justify-center pt-1">
              <button
                type="submit"
                disabled={isSubmitting || state.solution.trim().length === 0}
                className="inline-flex min-w-44 items-center justify-center rounded-xl bg-blue px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-blue/90 disabled:cursor-not-allowed disabled:opacity-65"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  )
}

export default function HackDetailPage() {
  const location = useLocation()
  useScrollToTopOnRouteEnter({ scrollOnPop: true })
  const navigate = useNavigate()
  const screenPath = `${location.pathname}${location.search || ''}`
  const { symptomId } = useParams<{ symptomId: string }>()
  const { user } = useAuthUser()
  const { hack, loading, error } = useHack(symptomId)
  const {
    whatWorks,
    loading: whatWorksLoading,
    error: whatWorksError,
    reload: reloadWhatWorks,
  } = useHackWhatWorks(symptomId)
  const [adminActionError, setAdminActionError] = useState<string | null>(null)
  const [voteError, setVoteError] = useState<string | null>(null)
  const [whatWorkActionError, setWhatWorkActionError] = useState<string | null>(null)
  const [activeVoteWhatWorkId, setActiveVoteWhatWorkId] = useState<string | null>(null)
  const [activeDeleteWhatWorkId, setActiveDeleteWhatWorkId] = useState<string | null>(null)
  const [whatWorkModalState, setWhatWorkModalState] = useState<WhatWorkModalState | null>(null)
  const [isSubmittingWhatWork, setIsSubmittingWhatWork] = useState(false)
  const [pendingScrollToWhatWorkId, setPendingScrollToWhatWorkId] = useState<string | null>(
    null,
  )
  const whatWorkRefs = useRef<Record<string, HTMLElement | null>>({})

  const authorIds = useMemo(
    () => whatWorks.map((item) => item.addedById),
    [whatWorks],
  )
  const { resolveDisplayName } = useUserProfiles(authorIds)

  useEffect(() => {
    if (!pendingScrollToWhatWorkId) {
      return
    }

    const targetElement = whatWorkRefs.current[pendingScrollToWhatWorkId]
    if (!targetElement) {
      return
    }

    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setPendingScrollToWhatWorkId(null)
  }, [pendingScrollToWhatWorkId, whatWorks])

  const openLoginModal = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', 'login')

    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const navigateBackToHacks = () => {
    if (location.key !== 'default') {
      navigate(-1)
      return
    }

    navigate('/hacks', { replace: true })
  }

  const handleEdit = () => {
    if (!hack?.uid) {
      return
    }
    navigate(`/hacks/${hack.uid}/edit`)
  }

  const handleDelete = async () => {
    if (!hack?.uid) {
      return
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this hack? The symptom will stay, but the hack video will be removed.',
    )
    if (!confirmed) {
      return
    }

    setAdminActionError(null)
    try {
      await deleteHackForSymptom(hack.uid)
      navigate('/hacks')
    } catch (deleteError) {
      setAdminActionError(
        deleteError instanceof Error ? deleteError.message : 'Failed to delete hack.',
      )
    }
  }

  const handleVote = async (
    whatWork: HackWhatWork,
    isPositive: boolean,
  ) => {
    if (!hack?.uid || !user?.uid) {
      openLoginModal()
      return
    }

    setVoteError(null)
    setActiveVoteWhatWorkId(whatWork.uid)
    try {
      const voteResult = await voteHackWhatWork(hack.uid, whatWork.uid, isPositive)
      if (
        isPositive
        && voteResult.userVote === 'up'
        && whatWork.addedById
      ) {
        void createHackSuggestionSelectedWorksNotification({
          forUserId: whatWork.addedById,
          senderId: user.uid,
          senderSlug: user.profileSlug ?? undefined,
          symptomId: hack.uid,
          whatWorkId: whatWork.uid,
          whatWorkTitle: whatWork.solution,
        })
      }
      await reloadWhatWorks()
    } catch (voteActionError) {
      setVoteError(
        voteActionError instanceof Error
          ? voteActionError.message
          : 'Failed to submit vote.',
      )
    } finally {
      setActiveVoteWhatWorkId(null)
    }
  }

  const openWhatWorkCreateModal = () => {
    if (!user?.uid) {
      openLoginModal()
      return
    }
    setWhatWorkActionError(null)
    setWhatWorkModalState({
      mode: 'create',
      solution: '',
      description: '',
    })
  }

  const openWhatWorkEditModal = (whatWork: HackWhatWork) => {
    if (!user?.uid) {
      openLoginModal()
      return
    }
    setWhatWorkActionError(null)
    setWhatWorkModalState({
      mode: 'edit',
      whatWorkId: whatWork.uid,
      solution: whatWork.solution ?? '',
      description: whatWork.description ?? '',
    })
  }

  const closeWhatWorkModal = () => {
    if (isSubmittingWhatWork) {
      return
    }
    setWhatWorkModalState(null)
    setWhatWorkActionError(null)
  }

  const handleWhatWorkSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!hack?.uid || !whatWorkModalState) {
      return
    }

    if (!user?.uid) {
      openLoginModal()
      return
    }

    const solution = whatWorkModalState.solution.trim()
    const description = whatWorkModalState.description.trim()
    if (!solution) {
      setWhatWorkActionError('Solution is required.')
      return
    }

    setWhatWorkActionError(null)
    setIsSubmittingWhatWork(true)
    try {
      if (whatWorkModalState.mode === 'create') {
        const createdWhatWorkId = await createHackWhatWork(hack.uid, {
          solution,
          description: description || undefined,
        })
        AnalyticsFeature.trackHackWhatWorksSubmit(
          screenPath,
          hack.uid,
          Boolean(user),
        )
        await reloadWhatWorks()
        setPendingScrollToWhatWorkId(createdWhatWorkId)
      } else if (whatWorkModalState.whatWorkId) {
        await updateHackWhatWork(hack.uid, whatWorkModalState.whatWorkId, {
          solution,
          description,
        })
        await reloadWhatWorks()
      }

      setWhatWorkModalState(null)
    } catch (saveError) {
      setWhatWorkActionError(
        saveError instanceof Error ? saveError.message : 'Failed to save your hack.',
      )
    } finally {
      setIsSubmittingWhatWork(false)
    }
  }

  const handleWhatWorkDelete = async (whatWork: HackWhatWork) => {
    if (!hack?.uid || !user?.uid) {
      openLoginModal()
      return
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete this suggested hack?',
    )
    if (!confirmed) {
      return
    }

    setWhatWorkActionError(null)
    setActiveDeleteWhatWorkId(whatWork.uid)
    try {
      await deleteHackWhatWork(hack.uid, whatWork.uid)
      await reloadWhatWorks()
    } catch (deleteError) {
      setWhatWorkActionError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Failed to delete suggested hack.',
      )
    } finally {
      setActiveDeleteWhatWorkId(null)
    }
  }

  if (loading) {
    return (
      <>
        <SeoHead
          title="Loading hack | Parkinson's Insights"
          canonicalPath={symptomId ? `/hacks/${symptomId}` : '/hacks'}
        />
        <LoadingSpinner className="min-h-[50vh]" />
      </>
    )
  }

  if (!hack) {
    return (
      <>
        <SeoHead
          title="Hack not found | Parkinson's Insights"
          canonicalPath={symptomId ? `/hacks/${symptomId}` : '/hacks'}
          noindex
        />
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
          <p className="text-lg font-semibold text-text-secondary">
            {error ?? 'Hack not found'}
          </p>
          <Link
            to="/hacks"
            className="mt-4 text-sm font-semibold text-primary hover:text-primary-light"
          >
            &larr; Back to Hacks
          </Link>
        </div>
      </>
    )
  }

  const hackName = hack.name?.trim()
  const hackTopicLabel = hackName || 'this symptom'
  const addHackCtaLabel = `Add your hack for ${hackTopicLabel}`
  const hackTitle = hackName
    ? `Michel's hack for ${hackName}`
    : "Michel's hack for this symptom"
  const hackDescription = sanitizeForMeta(
    `Watch Michel's Parkinson's hack${hackName ? ` for ${hackName}` : ''} and explore community suggestions for what works.`,
    170,
  )
  const normalizedVideoId = hack.videoId.trim()
  const hasHackVideo = normalizedVideoId.length > 0
  const ogImageUrl = hasHackVideo
    ? `https://i.ytimg.com/vi/${normalizedVideoId}/hqdefault.jpg`
    : undefined
  const canonicalPath = `/hacks/${hack.uid}`
  const hackYoutubeUrl = hack.michelsPdHacksVideoUrl?.trim() || (hasHackVideo
    ? `https://www.youtube.com/watch?v=${normalizedVideoId}`
    : '')
  const structuredData = buildHackStructuredData({
    hack,
    canonicalPath,
    hackTitle,
    hackDescription,
    ogImageUrl: ogImageUrl || undefined,
  })

  return (
    <article className="bg-background">
      <SeoHead
        title={`${hackTitle} | Michel's PD Hacks`}
        description={hackDescription}
        canonicalPath={canonicalPath}
        ogType={hasHackVideo ? 'video.other' : 'article'}
        imageUrl={ogImageUrl}
      />
      <StructuredData data={structuredData} />
      <div className="mx-auto w-full max-w-5xl px-4 pt-4 sm:px-6 sm:pt-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={navigateBackToHacks}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Hacks
          </button>

          {user?.isAdmin ? (
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleEdit}
                aria-label="Edit hack"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
              >
                <FiEdit2 className="h-5 w-5" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => {
                  void handleDelete()
                }}
                aria-label="Delete hack"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10"
              >
                <FiTrash2 className="h-5 w-5" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>

        <h1 className="mt-5 text-3xl font-bold tracking-tight text-text-primary sm:text-4xl">
          {hackTitle}
        </h1>
      </div>

      {hasHackVideo ? (
        <div className="mx-auto w-full max-w-md px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
          <YouTubeEmbed
            videoId={normalizedVideoId}
            title={hack.name}
            isShort
            className="shadow-none"
            iframeClassName="max-h-none"
          />
        </div>
      ) : (
        <div className="mx-auto w-full max-w-md px-4 pt-4 sm:px-6 sm:pt-5 lg:px-8">
          <div className="flex aspect-9/16 max-h-[400px] items-center justify-center rounded-xl bg-border-light px-4 text-center text-sm font-semibold text-text-muted">
            Video not available.
          </div>
        </div>
      )}

      {hackYoutubeUrl ? (
        <div className="mx-auto w-full max-w-md px-4 pt-4 text-center sm:px-6 sm:pt-5 lg:px-8">
          <a
            href={hackYoutubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              AnalyticsFeature.trackExternalLinkOpen(
                screenPath,
                'hack_detail',
                'hack_video',
                hack.uid,
                Boolean(user),
              )
            }}
            className="inline-flex items-center justify-center rounded-lg bg-blue px-6 py-3 text-sm font-semibold text-white no-underline transition-colors hover:bg-blue/90"
          >
            Open in YouTube
          </a>
        </div>
      ) : null}

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {adminActionError ? (
          <p className="mt-2 text-sm text-text-secondary">{adminActionError}</p>
        ) : null}
        {voteError ? (
          <p className="mt-2 text-sm text-text-secondary">{voteError}</p>
        ) : null}
        {whatWorkActionError ? (
          <p className="mt-2 text-sm text-text-secondary">{whatWorkActionError}</p>
        ) : null}

        <section className="mt-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-2xl font-bold text-text-primary">
              {`What helps others with ${hackTopicLabel}`}
            </h2>
            <button
              type="button"
              onClick={openWhatWorkCreateModal}
              aria-label={addHackCtaLabel}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-blue px-7 py-3.5 text-base font-bold leading-tight text-white transition-colors hover:bg-blue/90 sm:w-[18rem]"
            >
              <FiPlus className="h-5 w-5 shrink-0" />
              <span className="whitespace-normal text-center">{addHackCtaLabel}</span>
            </button>
          </div>

          {whatWorksLoading ? (
            <p className="mt-4 text-base font-semibold text-text-secondary">
              Loading what works entries...
            </p>
          ) : whatWorksError ? (
            <p className="mt-4 text-base font-semibold text-text-secondary">
              {whatWorksError}
            </p>
          ) : whatWorks.length === 0 ? (
            <p className="mt-4 text-base font-semibold text-text-secondary">
              No suggestions yet.
            </p>
          ) : (
            <div className="mt-4 space-y-3">
              {whatWorks.map((whatWork) => {
                const authorName = resolveDisplayName(whatWork.addedById)
                const canManageWhatWork =
                  user?.isAdmin === true || user?.uid === whatWork.addedById
                const isDeletePending = activeDeleteWhatWorkId === whatWork.uid

                return (
                  <article
                    key={whatWork.uid}
                    ref={(element) => {
                      whatWorkRefs.current[whatWork.uid] = element
                    }}
                    className="rounded-xl border border-border-light bg-surface p-4"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-text-primary">{authorName}</span>
                      {canManageWhatWork ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openWhatWorkEditModal(whatWork)}
                            aria-label="Edit suggested hack"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              void handleWhatWorkDelete(whatWork)
                            }}
                            disabled={isDeletePending}
                            aria-label="Delete suggested hack"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-500 bg-surface text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-65"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <p className="mt-3 text-base font-semibold text-text-primary sm:text-lg">
                      {whatWork.solution?.trim() || 'Untitled suggestion'}
                    </p>

                    {whatWork.description?.trim() ? (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-text-secondary">
                        {whatWork.description}
                      </p>
                    ) : null}

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            void handleVote(whatWork, true)
                          }}
                          disabled={activeVoteWhatWorkId === whatWork.uid}
                          className={`inline-flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                            whatWork.positiveRatingsIds?.includes(user?.uid ?? '')
                              ? 'bg-blue text-white'
                              : 'bg-border-light text-text-secondary hover:bg-blue/10 hover:text-blue'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          Works
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            void handleVote(whatWork, false)
                          }}
                          disabled={activeVoteWhatWorkId === whatWork.uid}
                          className={`inline-flex items-center rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                            whatWork.negativeRatingsIds?.includes(user?.uid ?? '')
                              ? 'bg-red-500 text-white'
                              : 'bg-border-light text-text-secondary hover:bg-red-50 hover:text-red-500'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                        >
                          Not for me
                        </button>
                        <span className="inline-flex items-center gap-2 rounded-full bg-border-light px-3 py-2 text-sm font-semibold text-text-secondary">
                          Score: {whatWork.totalVotes ?? 0}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          AnalyticsFeature.trackContentOpen(
                            screenPath,
                            'hack_comment_thread',
                            whatWork.uid,
                            'hack_detail',
                            Boolean(user),
                          )
                          navigate(
                            `/hacks/${hack.uid}/what-works/${whatWork.uid}/comments`,
                          )
                        }}
                        className="inline-flex items-center gap-2 rounded-full bg-blue px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
                      >
                        <FiMessageCircle className="h-4 w-4" />
                        Comments ({whatWork.commentsCount})
                      </button>
                    </div>
                  </article>
                )
              })}
            </div>
          )}
        </section>

      </div>
      <WhatWorkModal
        state={whatWorkModalState}
        isSubmitting={isSubmittingWhatWork}
        error={whatWorkActionError}
        onClose={closeWhatWorkModal}
        onSubmit={handleWhatWorkSubmit}
        onSolutionChange={(value) => {
          setWhatWorkActionError(null)
          setWhatWorkModalState((current) =>
            current ? { ...current, solution: value } : current,
          )
        }}
        onDescriptionChange={(value) => {
          setWhatWorkActionError(null)
          setWhatWorkModalState((current) =>
            current ? { ...current, description: value } : current,
          )
        }}
      />
    </article>
  )
}
